import * as React from "react";
import { Check, ChevronDown, Search, User as UserIcon } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "./utils";

export interface PersonOption {
  id: string | number;
  name: string;
  email?: string;
  jobRole?: string | null;
  department?: { id: string | number; departmentName: string } | null;
}

interface PersonSelectProps {
  people: PersonOption[];
  /** Selected person id, or "" for none. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/** Up to two initials — the avatar stand-in, since Idarah stores no profile photos. */
function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/** "Yardsman · Operations", skipping whichever half is missing. */
function subtitleOf(p: PersonOption) {
  return [p.jobRole, p.department?.departmentName].filter(Boolean).join(" · ");
}

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
        "bg-slate-100 text-[11px] font-semibold text-slate-600",
        "dark:bg-slate-800 dark:text-slate-300",
        className
      )}
    >
      {initials(name)}
    </span>
  );
}

/**
 * A person picker: avatar, name, job role and department, with type-ahead search.
 *
 * Built on Command/Popover rather than Select because a native-style listbox cannot
 * hold three lines of identity per row, and picking the right person out of a long
 * staff list needs search — scrolling an alphabetical list is not a real answer once
 * there are more than a screenful of people.
 *
 * Filtering is done here rather than by cmdk's built-in matcher, so the search covers
 * email, job role and department as well as the name — the same fields the row shows.
 * That is why `shouldFilter` is false, matching the existing SearchableSelect.
 */
export function PersonSelect({
  people,
  value,
  onChange,
  placeholder = "Select a person",
  searchPlaceholder = "Search by name, role, department or email…",
  emptyMessage = "No matching people.",
  disabled = false,
  id,
  className,
}: PersonSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selected = React.useMemo(
    () => people.find((p) => String(p.id) === value),
    [people, value]
  );

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return people;
    return people.filter((p) =>
      [p.name, p.email, p.jobRole, p.department?.departmentName]
        .filter(Boolean)
        .some((field) => (field as string).toLowerCase().includes(term))
    );
  }, [people, search]);

  // A stale search term would silently narrow the list the next time it opens.
  React.useEffect(() => { if (!open) setSearch(""); }, [open]);

  const handleSelect = (personId: string | number) => {
    onChange(String(personId));
    setOpen(false);
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input",
            "bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background",
            "focus:outline-none focus:ring-1 focus:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <Avatar name={selected.name} className="h-6 w-6 text-[10px]" />
              <span className="min-w-0 truncate">
                {selected.name}
                {subtitleOf(selected) && (
                  <span className="text-muted-foreground"> — {subtitleOf(selected)}</span>
                )}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground">
              <UserIcon size={16} />{placeholder}
            </span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-popover border border-border rounded-md shadow-md"
        align="start"
      >
        <Command shouldFilter={false} className="bg-transparent">
          {/* Not CommandInput: it carries its own search icon and border, and this
              needs the count row beneath it. */}
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search size={15} className="shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={cn(
                "h-10 w-full bg-transparent text-sm outline-none",
                "placeholder:text-muted-foreground"
              )}
            />
          </div>

          <CommandList className="max-h-72 overflow-y-auto" onWheel={(e) => e.stopPropagation()}>
            {filtered.length === 0 ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : (
              <div className="p-1">
                {filtered.map((p) => {
                  const subtitle = subtitleOf(p);
                  const isSelected = String(p.id) === value;
                  return (
                    <CommandItem
                      key={p.id}
                      value={String(p.id)}
                      onSelect={() => handleSelect(p.id)}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2",
                        "hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-accent/60"
                      )}
                    >
                      <Avatar name={p.name} />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium">{p.name}</span>
                        {subtitle && (
                          <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
                        )}
                        {p.email && (
                          <span className="truncate text-[11px] text-muted-foreground/80">
                            {p.email}
                          </span>
                        )}
                      </span>
                      {isSelected && <Check className="h-4 w-4 shrink-0" />}
                    </CommandItem>
                  );
                })}
              </div>
            )}
          </CommandList>

          <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
            {filtered.length} of {people.length} {people.length === 1 ? "person" : "people"}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default PersonSelect;
